import React, { useState, useEffect } from "react";
import { Box, LinearProgress, Typography, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { eye, eyeOff, eyeOffOutline, eyeOutline, person, personOutline } from "ionicons/icons";
import useNotificacion from "../../hooks/notificacion";
import { useSession } from "../../hooks/SessionContext";
import http from "../../hooks/httpAdapter";
import dayjs from "dayjs";
import StyledButton from "../../components/StyledButton/StyledButton";
import { Usuario } from "../../interfaces";
import {
  IonCol,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonRow,
} from "@ionic/react";
import { useHistory } from "react-router-dom";

interface ContainerProps {
  abrirModal: boolean;
  setAbrirModal?: (abrir: boolean) => void;
}

const LoginPopUp: React.FC<ContainerProps> = ({ abrirModal, setAbrirModal }) => {
  const [blnVerPassword, setBlnVerPassword] = useState<boolean>(false);
  const [isLoginPage, setIsLoginPage] = useState<boolean>(false);
  const [blnLogueoAutomatico, setBlnLogueoAutomatico] = useState<boolean>(false);

  const [errorUsuario, setErrorUsuario] = useState<boolean>(false);
  const [errorPassword, setErrorPassword] = useState<boolean>(false);
  const [msjErrorPassword, setMsjErrorPassword] = useState("");
  const { hcUL, setSesionInvalida, setHcUL, mostrarNotificacion } = useSession();
  const [usuario, setUsuario] = useState({
    usuario: "",
    password: "",
    token: "",
  });
  const history = useHistory();

  const handleChangeVisibilityOfPassword = () => {
    setBlnVerPassword(!blnVerPassword);
  };

  useEffect(() => {
    if (history.location.pathname === "/Login" || hcUL === null) {
      setIsLoginPage(true);
    } else {
      setIsLoginPage(false);
      setBlnLogueoAutomatico(true);
      setUsuario({ usuario: hcUL.usuario, password: hcUL.password, token: "" });
    }
  }, [history.location.pathname, abrirModal, hcUL]);

  const loginPaciente = async (usuario: string, password: string) => {
    try {
      const response = await http()
        .post(`Usuarios/Login`, {
          nombreUsuario: usuario,
          password: password,
        })

        .catch((e) => {
          console.error(e);
        });
      return response.token;
    } catch (error: any) {
      if (!blnLogueoAutomatico) {
        setErrorPassword(true);
        setMsjErrorPassword("Usuario o Password incorrecto");
        mostrarNotificacion(true, "Error: " + error.response, "rojo");
      } else {
      }
    }
  };

  const obtenerPrestador = async (token: string, documento: string) => {
    let prestador: Usuario;
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      const response = await http()
        .get(`Usuarios?usuario=${documento}`, config)
        .catch((e) => {
          console.error(e);
        });
      const data = response[0];
      prestador = {
        usuario: usuario?.usuario || "",
        password: usuario?.password || "",
        token: token,
        codigo: data.codigo,
        rol: data.rol,
        nombreUsuario: data.nombreUsuario,
        nombreCompleto: data.nombreCompleto,
        prestadorNom: data.prestadorNom,
        prestadorCod: data.prestadorCod,
        especialidadNom: data.especialidadNom,
        prestadores: data.prestadores,
        fechaSesion: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      };
      return prestador;
    } catch (error: any) {
      console.error("Error en petición obtenerPrestador", error.response);
      mostrarNotificacion(
        true,
        "Se produjo un error al intentar consultar los datos del prestador",
        "rojo"
      );
    }
  };

  const handleClickLogin = async () => {
    if (usuario !== null) {
      if (usuario.usuario !== "" && usuario.password !== "") {
        const token = await loginPaciente(usuario.usuario, usuario.password);
        const prestador = await obtenerPrestador(token, usuario.usuario);
        if (prestador) {
          mostrarNotificacion(true, "Sesión iniciada correctamente", "verde");
          setHcUL(prestador);
          setSesionInvalida(false);
          if (setAbrirModal) setAbrirModal(false);
          if (isLoginPage) history.push("/portal/");
        }
      } else {
        if (usuario.usuario !== "") setErrorUsuario(true);
        if (usuario.password !== "") {
          setErrorPassword(true);
          setMsjErrorPassword("Debe ingresar su contraseña");
        }
      }
    }
  };

  const handleClickSubmit = () => {
    handleClickLogin();
  };

  const handleChangeUsuario = (e: any) => {
    const value = e.target.value;
    setUsuario({ usuario: value, password: usuario.password, token: "" });
  };

  const handleChangePassword = (e: any) => {
    const value = e.target.value;
    setUsuario({ usuario: usuario.usuario, password: value, token: "" });
  };

  const handleRedirecccionarLogin = () => {
    history.push("/Login");
  };

  const [timeToRedirect, setTimeToRedirect] = useState(60);
  const [progress, setProgress] = useState(0);
  const [timerExpired, setTimerExpired] = useState(false); // Nuevo estado para controlar si el temporizador ha expirado
  let interval: NodeJS.Timeout;
  const secondsRemaining = Math.max(0, Math.round((1 - progress) * timeToRedirect));

  const startTimer = () => {
    interval = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = prevProgress + 1 / timeToRedirect;
        if (newProgress >= 1) {
          clearInterval(interval);
          setTimerExpired(true); // Marcar el temporizador como expirado
        }
        return newProgress;
      });
    }, 1000);
  };

  const resetTimer = () => {
    clearInterval(interval);
    setProgress(0);
    setTimerExpired(false);
    startTimer();
  };

  useEffect(() => {
    resetTimer();
  }, [timeToRedirect]);

  useEffect(() => {
    if (timerExpired) handleRedirecccionarLogin();
  }, [timerExpired]);

  return (
    <Dialog 
      open={abrirModal} 
      maxWidth="sm"
      fullWidth={false}
      PaperProps={{
        sx: {
          width: 400,
          maxWidth: '90vw',
          padding: 2,
          borderRadius: 2,
        }
      }}
    >
      {isLoginPage ? (
        <DialogTitle style={{ textAlign: "center", paddingBottom: "8px" }}>
          <h3 >Historias Clínicas de Pacientes</h3>
          <p >Inicio de sesión</p>
        </DialogTitle>
      ) : (
        <DialogTitle style={{ textAlign: "center", paddingBottom: "8px" }}>
          <p >Su sesión ha expirado, ¿desea extenderla?</p>
        </DialogTitle>
      )}

      <DialogContent style={{ paddingTop: "8px" }}>
        {blnLogueoAutomatico ? (
          <IonList>
            <StyledButton
              //@ts-ignore
              lines="none"
              size="large"
              className="verde justify-content-center"
              onClick={handleClickSubmit}
            >
              Reloguearse
            </StyledButton>
            <StyledButton
              //@ts-ignore
              lines="none"
              size="large"
              className="rojo justify-content-center"
              onClick={handleRedirecccionarLogin}
            >
              Cerrar sesion
            </StyledButton>
          </IonList>
        ) : (
          <IonList>
            <IonItem lines="none">
              <IonInput
                fill="solid"
                aria-label="Usuario"
                label="Usuario"
                labelPlacement="fixed"
                value={usuario.usuario}
                onKeyUp={handleChangeUsuario}
                autofocus
                type="text"
                style={{ paddingLeft: "5px" }}
              />
              <IonIcon aria-hidden="true" slot="end" ios={personOutline} md={person} />
            </IonItem>
            <IonItem lines="none" className={`${errorPassword && "ion-invalid"}`}>
              <IonInput
                fill="solid"
                aria-label="Contraseña"
                label="Contraseña"
                labelPlacement="fixed"
                value={usuario.password}
                onKeyUp={handleChangePassword}
                type={blnVerPassword ? "text" : "password"}
                className="passwordInput"
                errorText={msjErrorPassword}
                style={{ paddingLeft: "5px" }}
              />
              <IonIcon
                aria-hidden="true"
                slot="end"
                ios={blnVerPassword ? eyeOutline : eyeOffOutline}
                md={blnVerPassword ? eye : eyeOff}
                onClick={handleChangeVisibilityOfPassword}
              />
            </IonItem>

            <StyledButton
              //@ts-ignore
              lines="none"
              size="large"
              className="verde justify-content-center"
              onClick={handleClickSubmit}
            >
              Ingresar
            </StyledButton>
          </IonList>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LoginPopUp;
